import DSAuth from './dsAuth/DSAuth';
import { AMLErrorCode, authErrorString } from './AuthErrors';
import ctxsNative from './ctxsNative';
import { Logger } from './Logger';
import { storageConstants, stringConstants } from './AuthConstants';
import { logConstants } from '../utils/Constants';
import AuthUtils from './AuthUtils';
import {
	QueuedRequest,
	PrimaryTokenStatus,
	PrimaryTokenTableEntry,
	ProtectionMode,
	RequestParams,
	ResponseToNative,
} from './dsAuth/data/AuthTypes';
import { AuthStorageWrapper } from './AuthStorageWrapper';
import { NetworkLocation } from './TypeDefinitions';
import { SingleStoreConfiguration, StoreConfiguration } from '../Common/TypeDefinitions';
import StoreConfigManager from './StoreConfigManager';
import OIDCEndpointsManager from './oidc/OIDCEndpointsManager';
import {
	OIDCTokensTableKey,
	OIDCEndpointsTableKey,
	OIDCTokensTableEntry,
} from './oidc/TypeDefinitions';
import OIDCTokenManager from './oidc/OIDCTokenManager';
import OIDC from './oidc/OIDC';
import Utils from '../utils/Utils';
import { config } from './AuthConfig';
import { SentryLogger } from '../sentryLogging/SentryLogger';
import TokenManager, { PrimaryTokensUpdateReason } from './TokenManager';
import {
	AuthmanLiteReadyParams,
	GetCachedOIDCTokensEntryCompleteParams,
	SetLoggedoffCompleteParams,
	UrlTranslationType,
} from './main';
import CredentialsManager from './dsAuth/forms/CredentialsManager';
import GatewayService from './gateway/GatewayService';
import { StoreType } from './TypeDefinitions';
import { CookieManager } from './CookieManager';
import { HttpStatusCodes } from '../utils/http/HttpConstants';
import { environment } from './environment';
import NetworkLocationHandler from './beacon/NetworkLocationHandler';

interface webLogoffUrlObject {
	[referenceUri: string]: string;
}

export default class AuthMan {
	private static instance: AuthMan;
	private storeConfiguration: StoreConfiguration;
	public isAuthInProgress = false;
	public requestQueue: QueuedRequest[] = [];

	static getInstance(): AuthMan {
		return AuthMan.instance;
	}

	static async createInstance(): Promise<AuthMan> {
		await AuthStorageWrapper.getInstance();
		AuthMan.instance = new AuthMan();
		AuthMan.instance.storeConfiguration =
			await StoreConfigManager.getInstance().retrieveAllStoreConfigTableEntriesAsJSON();
		Logger.getInstance().write(
			logConstants.INFO,
			'storeConfig retrieved from db:' +
				JSON.stringify(AuthMan.instance.storeConfiguration)
		);
		return AuthMan.instance;
	}

	async clearCachedTokens(): Promise<void> {
		const authStorageWrapper = await AuthStorageWrapper.getInstance();
		const tables = [
			storageConstants.DOMAIN_COOKIE_TABLE,
			storageConstants.PRIMARY_TOKEN_TABLE,
			storageConstants.SECONDARY_TOKEN_TABLE,
			storageConstants.OIDC_TOKENS_TABLE,
		];
		await authStorageWrapper.clearObjectStores(tables);
	}

	async destroyTokensAndClearTables(
		id?: string,
		tables?: string[],
		protectMode?: ProtectionMode
	): Promise<void> {
		const authStorageWrapper = await AuthStorageWrapper.getInstance();
		Logger.getInstance().write(
			logConstants.INFO,
			'Destroying all auth tokens and clearing storage...'
		);
		await DSAuth.getInstance().destroyTokens(id, protectMode);
		await OIDC.getInstance().revokeAllRefreshTokens(id, protectMode);
		if (tables) await authStorageWrapper.clearObjectStores(tables);
		else await authStorageWrapper.clearAllObjectStores();
	}

	async destroyTokensAndClearTablesExceptCredentials(
		id?: string,
		protectMode?: ProtectionMode
	): Promise<void> {
		const tables = [
			storageConstants.DOMAIN_COOKIE_TABLE,
			storageConstants.PRIMARY_TOKEN_TABLE,
			storageConstants.SECONDARY_TOKEN_TABLE,
			storageConstants.OIDC_TOKENS_TABLE,
			storageConstants.OIDC_ENDPOINTS_TABLE,
			storageConstants.STORE_CONFIGURATION_TABLE,
		];
		await this.destroyTokensAndClearTables(id, tables, protectMode);
	}

	async clearInvalidCredentialsTableEntries(): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			'Clearing invalid credentials storage entries as per config...'
		);
		await CredentialsManager.eraseCredentialsNotAllowedByConfig();
	}

	private parseRquestParams(
		rawParams: string,
		body: string,
		headers: string,
		id: string,
		options?: Partial<RequestParams>
	): RequestParams {
		const requestParams = JSON.parse(rawParams);
		requestParams.headers = JSON.parse(headers);
		requestParams.id = id;
		requestParams.body = body;
		requestParams.returnSecondaryToken =
			typeof requestParams.returnSecondaryToken !== 'undefined'
				? JSON.parse(requestParams.returnSecondaryToken.toLowerCase())
				: false;
		requestParams.allowCredUi =
			typeof requestParams.allowCredUi !== 'undefined'
				? JSON.parse(requestParams.allowCredUi.toLowerCase())
				: true; //By default setting the value to true
		requestParams.headers = DSAuth.getInstance().addBasicHeaders(
			requestParams.headers ? requestParams.headers : {},
			id
		); //Adds basic headers
		requestParams.authDomain = AuthUtils.getInstance().getAuthDomainFromRequest(
			requestParams.url,
			requestParams.headers
		);
		requestParams.url = Utils.getInstance().getSafeUrl(requestParams.url);
		if (requestParams.referenceUri)
			requestParams.referenceUri = Utils.getInstance().getSafeUrl(
				requestParams.referenceUri
			);
		const mergedRequestParams: RequestParams = { ...requestParams, ...options };
		return mergedRequestParams;
	}

	/// <summary>
	/// make the url request and return response to native by calling ctxsNative.SetResponse or ctxsNative.SetResponseWithToken
	/// </summary>
	async startUrlRequest(
		rawParams: string,
		body: string,
		headers: string,
		id: string
	): Promise<void> {
		//DS Auth flow
		const paramToLog = Utils.getInstance().getRequestForLogs(rawParams);
		Logger.getInstance().write(logConstants.INFO, `${id}:Input params:${paramToLog}`);
		let requestParams;
		try {
			requestParams = this.parseRquestParams(rawParams, body, headers, id);
		} catch (e) {
			const errorResponse = AuthUtils.getInstance().constructAMLResponse({
				id: id,
				errorCode: AMLErrorCode.AML_INVALID_INPUT,
				errorMessage: 'Error parsing JSON parameter. Failed to execute request',
			});
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}:Error parsing JSON parameter. Failed to execute request. ${JSON.stringify(
					e
				)} \n`
			);

			SentryLogger.getInstance().logError(e);

			const errorResponseJsonString = JSON.stringify(errorResponse);
			ctxsNative.SetResponse(errorResponseJsonString);
			return;
		}
		const urlToprint = Utils.getInstance().removeQueryStringAndHashFromUrl(
			requestParams.url
		);
		const transactionId = AuthUtils.getInstance().getCitrixTransactionIdFromHeaders(
			requestParams.headers
		);

		Logger.getInstance().write(
			logConstants.INFO,
			`${id}: Transaction id: ${transactionId}`
		);

        // Add or modify the X-Citrix-Background-Request header based on allowCredUi value
        if (requestParams.allowCredUi === true) {
            requestParams.headers['X-Citrix-Background-Request'] = 'False';
        } else {
            requestParams.headers['X-Citrix-Background-Request'] = 'True';
        }

		let response: ResponseToNative;
		try {
			if (
				config.isUnifiedAuthenticationEnabled &&
				!requestParams.allowCredUi &&
				(await this.checkNetworkLocation(requestParams.referenceUri)) &&
				!GatewayService.getInstance().isSessionActive(requestParams.referenceUri)
			) {
				Logger.getInstance().write(
					logConstants.INFO,
					`Making an authenticated request with an inactive gateway session with allowCredUi false - ${requestParams}`
				);
				response = AuthUtils.getInstance().constructAMLResponse({
					id: requestParams.id,
					URLResponse: {
						statusCode: HttpStatusCodes.HTTP_STATUS_DENIED,
						body: '',
						headers: {},
					},
					errorCode: AMLErrorCode.AML_START_URL_REQUEST_EXCEPTION,
					errorMessage: authErrorString.GATEWAY_SESSION_EXPIRED_ALLOWCREDUI,
				});
			} else {
				response = await DSAuth.getInstance().startUrlRequest(requestParams);
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.INFO,
				`${id}: Transaction id: ${transactionId} Auth failed for url : ${urlToprint}`
			);
		}

		Logger.getInstance().write(
			logConstants.INFO,
			`${id}: Transaction id: ${transactionId} Auth Complete for url : ${urlToprint}`
		);

		const responseJsonString = JSON.stringify(response); //response string which is sent to native
		const responseToLog = Utils.getInstance().getResponseForLogs(response);
		Logger.getInstance().write(
			logConstants.DEBUG,
			`${id}:Transaction id: ${transactionId} JSON-stringified :${JSON.stringify(
				responseToLog
			)}`
		);

		if (requestParams.returnSecondaryToken) {
			ctxsNative.SetResponseWithToken(responseJsonString);
		} else {
			ctxsNative.SetResponse(responseJsonString);
		}
	}

	async getSecondaryTokenForUrl(
		rawParams: string,
		body: string,
		headers: string,
		id: string
	): Promise<void> {
		const paramToLog = Utils.getInstance().getRequestForLogs(rawParams);
		Logger.getInstance().write(
			logConstants.INFO,
			`${id}: Fetching secondary token for Input params :${paramToLog}`
		);
		let requestParams;
		try {
			requestParams = this.parseRquestParams(rawParams, body, headers, id, {
				returnSecondaryToken: true,
			});
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}:Error parsing raw params for fetching secondary token. ${JSON.stringify(
					e
				)}`
			);
			const errorResponse =
				AuthUtils.getInstance().constructAMLSecondaryTokenResponseToNative({
					id,
					errorCode: AMLErrorCode.AML_INVALID_INPUT,
					errorMessage: 'Error parsing JSON parameter. Failed to get secondary token',
				});
			SentryLogger.getInstance().logError(e);
			const errorResponseJsonString = JSON.stringify(errorResponse);
			ctxsNative.getSecondaryTokenForUrlComplete(errorResponseJsonString);
			return;
		}
		const urlToprint = Utils.getInstance().removeQueryStringAndHashFromUrl(
			requestParams.url
		);
		const transactionId = AuthUtils.getInstance().getCitrixTransactionIdFromHeaders(
			requestParams.headers
		);
		Logger.getInstance().write(
			logConstants.INFO,
			`${id}: Transaction id: ${transactionId}`
		);
		const response = await DSAuth.getInstance().getSecondaryTokenForUrl(requestParams);
		Logger.getInstance().write(
			logConstants.INFO,
			`${id}: Transaction id: ${transactionId} secondary token fetch complete for url : ${urlToprint}`
		);
		const responseJsonString = JSON.stringify(response);
		ctxsNative.getSecondaryTokenForUrlComplete(responseJsonString);
	}

	/// <summary>
	/// pass web view response along
	/// </summary>
	async ctxsExitWebview(response: string, referenceUri: string): Promise<void> {
		AuthMan.getInstance().handleResponseForFirstRequest(referenceUri, response);
	}

	private async checkNetworkLocation(referenceUri: string): Promise<boolean> {
		if (config.isSimplifyBeaconLogicEnabled) {
			return (
				this.storeConfiguration[referenceUri]?.storeType !== StoreType.Cloud &&
				(await NetworkLocationHandler.getInstance().GetNetworkLocationForStore(
					referenceUri
				)) == NetworkLocation.Outside
			);
		} else {
			return (
				this.storeConfiguration[referenceUri]?.storeType === StoreType.Gateway &&
				this.storeConfiguration[referenceUri]?.networkLocation !== NetworkLocation.Inside
			);
		}
	}

	private async getWebLogoffUrlForReferenceUri(
		referenceUri: string
	): Promise<webLogoffUrlObject | null> {
		const tokenObj = await TokenManager.getInstance().getPrimaryTokenForStore(
			referenceUri
		);
		return tokenObj && !!tokenObj.webLogoffUrl
			? { [referenceUri]: tokenObj.webLogoffUrl }
			: null;
	}

	private async getWebLogoffUrls(): Promise<webLogoffUrlObject[]> {
		const result: webLogoffUrlObject[] = [];
		const storeConfigs = AuthMan.getInstance().getStoreConfiguration();
		if (!storeConfigs) {
			return result;
		}
		const referenceUris = Object.keys(storeConfigs);
		for (const referenceUri of referenceUris) {
			const webLogoffUrlObj = await this.getWebLogoffUrlForReferenceUri(referenceUri);
			if (webLogoffUrlObj) {
				result.push(webLogoffUrlObj);
			}
		}
		return result;
	}

	/// <summary>
	/// Revoke and delete all tokens and cookies from the db and then notify native
	/// </summary>
	async handleLogoff(id: string): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			id + ': Start Clearing all cached tokens and cookies'
		);
		const setLoggedOffCompleteParams: SetLoggedoffCompleteParams = {};
		try {
			const webLogoffUrlsObjs = await this.getWebLogoffUrls();
			for (const obj of webLogoffUrlsObjs) {
				for (const [referenceUri, url] of Object.entries(obj)) {
					setLoggedOffCompleteParams[referenceUri] =
						setLoggedOffCompleteParams[referenceUri] ?? {};
					setLoggedOffCompleteParams[referenceUri].weblogoffUrl = url;
				}
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error while parsing webLogoffUrl from tokens. ' + e.message
			);
		}

		const tables = [
			storageConstants.DOMAIN_COOKIE_TABLE,
			storageConstants.PRIMARY_TOKEN_TABLE,
			storageConstants.SECONDARY_TOKEN_TABLE,
			storageConstants.OIDC_TOKENS_TABLE,
		];

		await this.destroyTokensAndClearTables(id, tables);

		if (config.isUnifiedAuthenticationEnabled) {
			await GatewayService.getInstance().signoutOfActiveSessions();
		}

		Logger.getInstance().write(
			logConstants.INFO,
			id + ': Done Clearing all cached tokens and cookies'
		);
		Logger.getInstance().write(
			logConstants.INFO,
			'setLoggedOffCompleteParams: ' + JSON.stringify(setLoggedOffCompleteParams)
		);
		await ctxsNative.SetLoggedOffComplete(id, JSON.stringify(setLoggedOffCompleteParams));
	}

	/// <summary>
	/// For a given store revoke and delete all tokens and cookies from the db and then notify native
	/// </summary>
	async handleLogOffForStore(id: string, referenceUri: string): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			id + ': Start Clearing tokens and cookies for store ' + referenceUri
		);
		const storeConfig = this.storeConfiguration[referenceUri];
		const setLoggedOffCompleteParams: SetLoggedoffCompleteParams = {};
		try {
			const webLogoffUrlsObj = await this.getWebLogoffUrlForReferenceUri(referenceUri);
			if (webLogoffUrlsObj) {
				setLoggedOffCompleteParams[referenceUri] = {
					weblogoffUrl: webLogoffUrlsObj[referenceUri],
				};
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Error while parsing webLogoffUrl from tokens. ' + e.message
			);
		}

		if (storeConfig) {
			const authDomain = storeConfig.authDomain ? storeConfig.authDomain : '';
			await DSAuth.getInstance().destroyTokensForStore(id, referenceUri);
			await TokenManager.getInstance().deleteTokensForStore(referenceUri, authDomain);

			if (
				!config.isUnifiedAuthenticationEnabled ||
				storeConfig?.storeType === StoreType.Cloud
			) {
				const serviceURL = storeConfig.tokenServiceURL;
				const oidcConfig = storeConfig.oidcConfiguration;
				const oidcTokenTableKey: OIDCTokensTableKey = {
					referenceUri: referenceUri,
					authDomain,
				};
				const oidcEndPointTableKey: OIDCEndpointsTableKey = {
					serviceURL: serviceURL,
					authDomain: authDomain,
					referenceUri: referenceUri,
				};
				await OIDC.getInstance().revokeRefreshToken(
					oidcEndPointTableKey,
					oidcTokenTableKey,
					oidcConfig,
					id
				);
				await OIDCTokenManager.getInstance().deleteOIDCTokens(oidcTokenTableKey);
			}

			if (config.isUnifiedAuthenticationEnabled) {
				if (storeConfig?.storeType === StoreType.StoreFront) {
					await CredentialsManager.eraseCredentialsForStore(referenceUri);
					await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
						referenceUri
					);
				} else if (storeConfig?.storeType === StoreType.Gateway) {
					await GatewayService.getInstance().removeGatewaySession(referenceUri);
					await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
						referenceUri
					);
				}
			}

			Logger.getInstance().write(
				logConstants.INFO,
				id + ': Done Clearing all cached tokens and cookies for store ' + referenceUri
			);
		} else {
			Logger.getInstance().write(
				logConstants.ERROR,
				id + ': Store configuration of the refence uri ' + referenceUri + ' not found'
			);
		}
		await ctxsNative.SetLoggedOffComplete(id, JSON.stringify(setLoggedOffCompleteParams));
	}

	/// <summary>
	/// check if AML has a primary token for given service url
	/// </summary>
	async isLoggedOnToAMLService(id: string, serviceURL: string): Promise<void> {
		const isLoggedOn: boolean = await DSAuth.getInstance().isLoggedOnToAMLService(
			id,
			serviceURL
		);
		ctxsNative.SetLoggedOnState(id, isLoggedOn.toString());
	}

	/// <summary>
	/// returns storeConfiguration
	/// </summary>
	getStoreConfiguration(): StoreConfiguration {
		return this.storeConfiguration;
	}

	/// <summary>
	/// returns store name of store with given reference uri
	/// </summary>
	getStoreName(referenceUri: string): string {
		const storeConfig = this.getStoreConfiguration();
		return storeConfig[referenceUri]?.storeName;
	}

	async getFetchInstanceFromStoreConfiguration(
		referenceUri: string
	): Promise<GatewayService['fetch'] | DSAuth['fetch']> {
		const storeConfiguration = this.getStoreConfiguration();
		let networkLocation: NetworkLocation;
		if (config.isSimplifyBeaconLogicEnabled) {
			networkLocation =
				await NetworkLocationHandler.getInstance().GetNetworkLocationForStore(
					referenceUri
				);
		} else {
			networkLocation = storeConfiguration[referenceUri]?.networkLocation;
		}

		if (
			config.isUnifiedAuthenticationEnabled &&
			storeConfiguration &&
			storeConfiguration[referenceUri]
		) {
			if (
				storeConfiguration[referenceUri]?.storeType !== StoreType.Cloud &&
				networkLocation !== NetworkLocation.Inside
			) {
				return GatewayService.getInstance().fetch.bind(GatewayService.getInstance());
			}
		}

		return DSAuth.getInstance().fetch.bind(DSAuth.getInstance());
	}

	/// <summary>
	/// 1. parses store config
	/// 2. checks for auth domain change by comparing auth domain corresponding to referenceUri sent by native against what is present in memory
	/// deletes tokens of older auth domain (the one present in db), if the above are not matching
	/// 3. updates the configuration member variable of AuthMan class and the storeconfiguration table in DB
	/// </summary>
	async setStoreConfiguration(
		configuration: string,
		retainOldStores = false,
		mergeStores = false
	): Promise<void> {
		const newStoreConfig =
			StoreConfigManager.getInstance().parseStoreConfig(configuration);
		for (const key of Object.keys(newStoreConfig)) {
			if ('tokenServiceURL' in newStoreConfig[key] === false) {
				Logger.getInstance().write(
					logConstants.ERROR,
					'tokenServiceURL missing. setStoreConfiguration will fail for ' +
						newStoreConfig[key]
				);
				continue;
			}
		}
		await this.handleAuthDomainChange(newStoreConfig);
		await this.handleClientIdChange(newStoreConfig);

		if (retainOldStores) {
			if (mergeStores) {
				for (const refUri of Object.keys(newStoreConfig)) {
					this.storeConfiguration[refUri] =
						AuthUtils.getInstance().mergeStoreConfigurations(
							newStoreConfig[refUri],
							this.storeConfiguration[refUri]
						);
				}
			} else {
				for (const refUri of Object.keys(newStoreConfig)) {
					this.storeConfiguration[refUri] = newStoreConfig[refUri];
				}
			}
		} else {
			this.storeConfiguration = newStoreConfig;
		}

		await StoreConfigManager.getInstance().saveStoreConfig(this.storeConfiguration);
	}

	async deleteStoreConfiguration(refUri: string): Promise<void> {
		if (refUri in this.storeConfiguration) {
			delete this.storeConfiguration[refUri];
			await StoreConfigManager.getInstance().saveStoreConfig(this.storeConfiguration);
		} else {
			Logger.getInstance().write(
				logConstants.ERROR,
				stringConstants['ERROR_MISSING_REFERENCE_URI']
			);
		}
	}

	/**
	 * Checks if oidc client id has changed and deleted older oidc tokens if it has changed
	 *
	 * @private
	 * @param {StoreConfiguration} newConfiguration
	 * @return {*}  {Promise<void>}
	 * @memberof AuthMan
	 */
	private async handleClientIdChange(
		newConfiguration: StoreConfiguration
	): Promise<void> {
		let referenceUri;
		try {
			for (referenceUri of Object.keys(newConfiguration)) {
				if (newConfiguration[referenceUri].oidcConfiguration) {
					let currentClientId;
					const newClientId = StoreConfigManager.getInstance().getClientid(
						newConfiguration[referenceUri].oidcConfiguration,
						config.platform
					);
					if (
						this.storeConfiguration &&
						this.storeConfiguration[referenceUri] &&
						this.storeConfiguration[referenceUri].oidcConfiguration
					) {
						currentClientId = StoreConfigManager.getInstance().getClientid(
							this.storeConfiguration[referenceUri].oidcConfiguration,
							config.platform
						);
					}
					if (currentClientId && newClientId && currentClientId != newClientId) {
						Logger.getInstance().write(
							logConstants.INFO,
							'OIDC client id change detected for store ' + referenceUri
						);
						const oidcTokenTableKey = {
							referenceUri: referenceUri,
							authDomain: this.storeConfiguration[referenceUri].authDomain,
						} as OIDCTokensTableKey;
						await OIDCTokenManager.getInstance().deleteOIDCTokens(oidcTokenTableKey);
						Logger.getInstance().write(
							logConstants.INFO,
							'completed deleting oidc tokens for store ' + referenceUri
						);
					}
				}
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
			Logger.getInstance().write(
				logConstants.ERROR,
				stringConstants['ERROR_HANDLING_OIDC_CLIENTID_CHANGE']
			);
		}
	}

	private async handleAuthDomainChange(
		newConfiguration: StoreConfiguration
	): Promise<void> {
		let referenceUri;
		try {
			for (referenceUri of Object.keys(newConfiguration)) {
				const newAuthDomain = newConfiguration[referenceUri].authDomain;
				const newTokenServiceUrl = newConfiguration[referenceUri].tokenServiceURL;
				const authDomainInNewTokenServiceUrl =
					AuthUtils.getInstance().getAuthDomainFromTokenServiceUrl(newTokenServiceUrl);
				if (this.storeConfiguration && this.storeConfiguration[referenceUri]) {
					const currentTokenServiceURL =
						this.storeConfiguration[referenceUri].tokenServiceURL;
					const authDomainInCurrentTokenServiceUrl =
						AuthUtils.getInstance().getAuthDomainFromTokenServiceUrl(
							currentTokenServiceURL
						);
					const currentAuthDomain = this.storeConfiguration[referenceUri].authDomain;
					if (
						currentTokenServiceURL &&
						currentAuthDomain &&
						(currentAuthDomain != newAuthDomain ||
							authDomainInCurrentTokenServiceUrl != authDomainInNewTokenServiceUrl)
					) {
						Logger.getInstance().write(
							logConstants.INFO,
							'Auth domain changed for store corresponding to referenceUri ' +
								referenceUri
						);
						const oidcTokenTableKey = {
							referenceUri: referenceUri,
							authDomain: currentAuthDomain,
						} as OIDCTokensTableKey;
						const oidcEndPointTableKey = {
							serviceURL: currentTokenServiceURL,
							authDomain: currentAuthDomain,
							referenceUri: referenceUri,
						} as OIDCEndpointsTableKey;
						await OIDC.getInstance().revokeRefreshToken(
							oidcEndPointTableKey,
							oidcTokenTableKey,
							this.storeConfiguration[referenceUri].oidcConfiguration,
							undefined
						);
						await DSAuth.getInstance().deleteTokensAndCookiesForStore(
							referenceUri,
							currentAuthDomain
						);
						await OIDCEndpointsManager.getInstance().deleteOIDCEndpoints(
							oidcEndPointTableKey
						);
						await OIDCTokenManager.getInstance().deleteOIDCTokens(oidcTokenTableKey);
						Logger.getInstance().write(
							logConstants.INFO,
							'completed deleting all tokens and cookies for store ' + referenceUri
						);
					}
				}
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e, { tags: { url: referenceUri } });
			Logger.getInstance().write(
				logConstants.ERROR,
				stringConstants['ERROR_HANDLING_AUTHDOMAIN_CHANGE']
			);
		}
	}

	//#region functions to process the request queue

	/*
	An overview of the request queue:
	1. In AuthWebViewHandler.start, the incoming request is added to queue and if auth is not in progress openChildTab is called to open the webview
	2. In ctxsExitWebview(DSAuth.ts), when the response is returned by SSP, it checks for 2 cases:
		a. If auth was cancelled, clearQueueAndProcessNextItem is called.
		The function of clearQueueAndProcessNextItem is to clear all requests for a given referenceUri from the queue
		and if requests for any other referenceUri are pending, to allow them to prompt for auth.
		b. If auth was successful, only the first request for that referenceUri is signalled to proceed with storing the primary token.
		This is done by calling handleResponseForFirstRequest, which just resolves the first request and removes it from the queue.
	3. When the first request (which triggered auth) is signalled (in PrimaryTokenService.ts::GetPrimaryTokenForURL), 
		it goes to complete the primary token flow and store the primary token in indexeddb.
			a. If the resume function completed successfully, clearQueueAndProcessNextItem is called. This time it
			signals to all other requests for that referenceUri that the primary token is already available and so they
			can skip the resume function.
			b. If the resume function fails for some reason, it throws an error and queues a task to call clearQueueAndProcessNextItem
			and resolve all the pending requests for that referenceUri with the appropriate error code.
	*/

	/**
	 *Resolves all pending requests for a given referenceUri and removes them from the request queue.
	 *Then, proceeds to pick the next request from the queue (if any) and trigger authentication for it.
	 * @param {string} referenceUri the referenceUri for which requests are to be resolved
	 * @param {string} response the response with which the requests are to be resolved
	 */
	clearQueueAndProcessNextItem(referenceUri: string, response: PrimaryTokenStatus): void {
		//filteredRequests are the ones which need to be resolved
		const filteredRequests = this.requestQueue.filter(
			(req) => req.referenceUri === referenceUri
		);
		response.shouldClearQueue = false;
		filteredRequests.forEach((req, index) => {
			//index 0 is resolved by handleResponseForFirstRequest
			if (index !== 0) {
				Logger.getInstance().write(
					logConstants.INFO,
					`${req.id}:calling waitStatusResolve with ${JSON.stringify(response)}`
				);
				req.waitStatusResolve(response);
			}
		});

		this.isAuthInProgress = false;

		//removing the filtered requests from the requestQueue
		this.requestQueue = this.requestQueue.filter(
			(req) => req.referenceUri !== referenceUri
		);
		// Start processing requests for any other stores
		if (this.requestQueue.length > 0) {
			const status = new PrimaryTokenStatus(
				stringConstants.RESUME_PRIMARY_TOKEN_OR_AAAC_FETCH,
				true
			);
			Logger.getInstance().write(
				logConstants.INFO,
				`${this.requestQueue[0].id}:calling waitStatusResolve with ${JSON.stringify(
					status
				)}`
			);
			this.requestQueue[0].waitStatusResolve(status);
		}
	}

	/**
	 *Resolves the first request for `referenceUri` that triggered the authentication prompt with the response returned by the client.
	 *This function is called from `AuthMan.ctxsExitWebview` when the authentication webview is closed.
	 * @param {string} referenceUri the referenceUri for the request to be resolved
	 * @param {string} response the value with which the request is to be resolved
	 */
	handleResponseForFirstRequest(referenceUri: string, response: string): void {
		//TODO: pass id instead of getting first request from queue
		const firstReqIndex = this.requestQueue.findIndex(
			(req) => req.referenceUri === referenceUri && req.webviewResolve
		);
		// resolve the first req so it can store the primary token before resolving other requests for the same store
		if (firstReqIndex !== -1) {
			this.requestQueue[firstReqIndex].webviewResolve(response);
		}
	}

	async getCachedPrimaryTokenEntry(
		id: string,
		authdomain: string,
		referenceUri: string,
		decryptToken: boolean
	): Promise<void> {
		const tokenObj = await TokenManager.getInstance().getCachedPrimaryToken(
			authdomain,
			referenceUri,
			id,
			decryptToken
		);
		ctxsNative.getCachedPrimaryTokenEntryComplete(id, JSON.stringify(tokenObj));
	}

	async getCachedOIDCTokensEntry(
		id: string,
		authDomain: string,
		referenceUri: string,
		decryptTokens: boolean
	): Promise<void> {
		let oidcTokensObj: GetCachedOIDCTokensEntryCompleteParams = null;
		try {
			if (config.longLivedTokenSupport) {
				const cachedTokens = await OIDCTokenManager.getInstance().retrieveOIDCTokens(
					{
						authDomain,
						referenceUri,
					},
					decryptTokens
				);
				if (cachedTokens) {
					const storeConfig = this.storeConfiguration[referenceUri];
					const serviceURL = storeConfig?.tokenServiceURL;
					const oidcEndPointTableKey: OIDCEndpointsTableKey = {
						serviceURL,
						authDomain,
						referenceUri,
					};
					const { revocationEndpoint } =
						(await OIDCEndpointsManager.getInstance().retrieveOIDCEndpoints(
							oidcEndPointTableKey
						)) || {};

					oidcTokensObj = {
						...cachedTokens,
						revocationEndpoint,
					};
				}
			}
		} catch (e) {
			Logger.getInstance().write(logConstants.ERROR, 'Failed to retrieve oidc tokens');
		}
		ctxsNative.getCachedOIDCTokensEntryComplete(id, JSON.stringify(oidcTokensObj));
	}

	async storePrimaryTokenEntry(
		tokenObj: PrimaryTokenTableEntry,
		encryptToken: boolean,
		reason: PrimaryTokensUpdateReason
	): Promise<void> {
		const tokenManager = TokenManager.getInstance();
		if (tokenObj && !tokenManager.isTokenExpired(tokenObj)) {
			await tokenManager.storePrimaryTokenEntry(tokenObj, encryptToken, reason);
		}
	}

	async storeOIDCTokensEntry(
		id: string,
		oidcTokensEntry: OIDCTokensTableEntry,
		encryptTokens: boolean
	): Promise<void> {
		if (config.longLivedTokenSupport) {
			const {
				referenceUri,
				authDomain,
				serviceURL: tokenServiceUrl,
				accessToken,
				expiresAt,
				tokenType,
				refreshToken,
				scopes,
			} = oidcTokensEntry;
			await OIDCTokenManager.getInstance().saveOIDCTokens(
				{
					authDomain,
					referenceUri,
				},
				{ accessToken, expiresAt, tokenType, refreshToken },
				tokenServiceUrl,
				scopes,
				'native',
				encryptTokens
			);
		} else {
			Logger.getInstance().write(
				logConstants.INFO,
				`id: ${id}, saveOIDCTokens skipped as LLT is disabled`
			);
		}
	}

	getGatewaySessionCookie(id: string, url: string): void {
		if (!url) {
			Logger.getInstance().write(
				logConstants.INFO,
				`getSessionCookie: id: ${id}, url in params is null`
			);
			environment.getSessionCookieComplete(id, '');
			return;
		}

		const cookie = GatewayService.getInstance().getSessionCookie(url);
		environment.getSessionCookieComplete(id, cookie);
	}

	getGatewayTranslatedUrl(id: string, url: string): void {
		if (!url) {
			Logger.getInstance().write(
				logConstants.INFO,
				`getTranslatedUrl: id: ${id}, url in params is null`
			);
			environment.getTranslatedUrlComplete(id, '');
			return;
		}

		const translatedUrl = GatewayService.getInstance().getTranslatedUrl(url);
		environment.getTranslatedUrlComplete(id, translatedUrl);
	}

	async translateURL(
		url: string,
		referenceUri: string,
		translationType: UrlTranslationType
	): Promise<string> {
		return GatewayService.getInstance().translateUrl(url, referenceUri, translationType);
	}

	async evaluateNetworkLocationAndStoreConfiguration(params: string): Promise<void> {
		const { internalBeacon, discoveryURL, storeType } = JSON.parse(params);
		const storeConfigForStore: SingleStoreConfiguration = {
			tokenServiceURL: null,
			oidcConfiguration: null,
			authDomain: null,
			storeType,
			beacon: internalBeacon,
			networkLocation: null,
		};

		const storeConfigMap = new Map<string, SingleStoreConfiguration>();
		storeConfigMap.set(discoveryURL, storeConfigForStore);

		const storeConfigObj: { [key: string]: SingleStoreConfiguration } = {};
		storeConfigMap.forEach((value, key) => {
			storeConfigObj[key] = value;
		});

		// Save the store configuration in the DB with internal beacon and storeType
		await this.setStoreConfiguration(JSON.stringify(storeConfigObj), true, true);

		const networkLocation =
			await NetworkLocationHandler.getInstance().GetNetworkLocationForStore(
				discoveryURL,
				internalBeacon
			);

		const storeConfigForStoreWithLocation: SingleStoreConfiguration = {
			tokenServiceURL: null,
			oidcConfiguration: null,
			authDomain: null,
			storeType,
			beacon: internalBeacon,
			networkLocation,
		};

		storeConfigMap.clear();
		storeConfigMap.set(discoveryURL, storeConfigForStoreWithLocation);

		const storeConfigObjWithLocation: { [key: string]: SingleStoreConfiguration } = {};
		storeConfigMap.forEach((value, key) => {
			storeConfigObjWithLocation[key] = value;
		});

		// Saving the store configuration again with network location
		await this.setStoreConfiguration(
			JSON.stringify(storeConfigObjWithLocation),
			true,
			true
		);
	}

	async reevaluateBeaconsOnNetworkChange(params: string): Promise<void> {
		const { referenceUri } = JSON.parse(params);
		await NetworkLocationHandler.getInstance().ReevaluateBeaconsOnNetworkChange(
			referenceUri
		);
	}

	/// <summary>
	/// sets the AML initialisation complete and indicates it to native
	/// </summary>
	static async ready(errorObj: AuthmanLiteReadyParams): Promise<void> {
		ctxsNative.AuthmanLiteReady(JSON.stringify(errorObj));
	}
	//#endregion
}
